import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Group, Loader, Paper, ScrollArea, Stack, Text, Textarea } from '@mantine/core';
import { IconSend, IconSparkles } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

// المساعد الذكي — chat over the tenant's own numbers. The server runs a
// tool-calling loop against fixed tenant-scoped aggregations; the model
// never sees other tenants or writes queries.

interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
}

export function AiAssistantPage() {
  const { t } = useTranslation();
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [draft, setDraft] = useState('');
  const viewportRef = useRef<HTMLDivElement>(null);

  const ask = useMutation({
    mutationFn: async (question: string) =>
      (
        await apiClient.post<{ answer: string }>('/api/ai/ask', {
          question,
          history: turns.slice(-8),
        })
      ).data,
    onSuccess: (response) => {
      setTurns((prev) => [...prev, { role: 'assistant', text: response.answer }]);
      scrollDown();
    },
    onError: (err) => {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const text =
        status === 503
          ? t('ai.notConfigured')
          : status === 403
            ? t('ai.notLicensed')
            : t('ai.failed');
      setTurns((prev) => [...prev, { role: 'assistant', text }]);
      scrollDown();
    },
  });

  const scrollDown = () =>
    setTimeout(() => {
      viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight, behavior: 'smooth' });
    }, 50);

  const send = () => {
    const question = draft.trim();
    if (!question || ask.isPending) return;
    setTurns((prev) => [...prev, { role: 'user', text: question }]);
    setDraft('');
    scrollDown();
    ask.mutate(question);
  };

  const suggestions = [
    t('ai.suggest1'),
    t('ai.suggest2'),
    t('ai.suggest3'),
  ];

  return (
    <Stack gap="lg" h="calc(100vh - 140px)">
      <PageHeader
        eyebrow={t('nav.sections.overview')}
        title={t('ai.title')}
        subtitle={t('ai.subtitle')}
      />

      <Paper p="md" className="flex-1 flex flex-col min-h-0">
        <ScrollArea className="flex-1" viewportRef={viewportRef}>
          {turns.length === 0 ? (
            <Stack align="center" py="xl" gap="sm">
              <IconSparkles size={32} stroke={1.4} color="var(--wheels-accent)" />
              <Text c="dimmed" size="sm">
                {t('ai.empty')}
              </Text>
              <Group gap="xs">
                {suggestions.map((suggestion) => (
                  <Paper
                    key={suggestion}
                    px="sm"
                    py={6}
                    withBorder
                    className="cursor-pointer hover:border-[var(--wheels-accent)]"
                    onClick={() => setDraft(suggestion)}
                  >
                    <Text size="sm">{suggestion}</Text>
                  </Paper>
                ))}
              </Group>
            </Stack>
          ) : (
            <Stack gap="sm" p="xs">
              {turns.map((turn, i) => (
                <div
                  key={i}
                  className={turn.role === 'user' ? 'self-end max-w-[80%]' : 'self-start max-w-[80%]'}
                >
                  <Paper
                    px="md"
                    py="sm"
                    radius="lg"
                    withBorder={turn.role === 'assistant'}
                    bg={turn.role === 'user' ? 'var(--wheels-accent)' : 'white'}
                  >
                    <Text
                      size="sm"
                      c={turn.role === 'user' ? 'white' : 'var(--wheels-ink-strong)'}
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      {turn.text}
                    </Text>
                  </Paper>
                </div>
              ))}
              {ask.isPending && (
                <Group gap="xs">
                  <Loader size="xs" color="wheelsRed" />
                  <Text size="xs" c="dimmed">
                    {t('ai.thinking')}
                  </Text>
                </Group>
              )}
            </Stack>
          )}
        </ScrollArea>

        <Group gap="sm" mt="sm" align="flex-end">
          <Textarea
            className="flex-1"
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            placeholder={t('ai.placeholder')}
            autosize
            minRows={1}
            maxRows={4}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
          />
          <ActionIcon
            size={42}
            color="wheels"
            variant="filled"
            aria-label={t('ai.send')}
            disabled={!draft.trim() || ask.isPending}
            onClick={send}
          >
            <IconSend size={18} stroke={1.8} />
          </ActionIcon>
        </Group>
      </Paper>
    </Stack>
  );
}
